import { synthesizeSpeech } from 'utils/helpers';

// Arabic: Google TTS (question text now comes from the API, so the old
// pre-recorded clips in public/quiz/voice no longer cover it).
// English: the browser's speechSynthesis with a selectable voice.
const ARABIC_VOICE = 'ar-XA-Wavenet-D';
const AUDIO_CACHE_LIMIT = 40;

const FEMALE =
  /hoda|salma|laila|layla|fatima|zariyah|zira|sara|sarah|samantha|female|woman|google العربية|google.*arabic/i;
const MALE = /naayf|nasser|maged|majed|tarik|mark|david|daniel|male|man|ماجد|طارق/i;

let token = 0;
let currentAudio = null;
const audioCache = new Map(); // text -> Promise<base64 mp3>

function allVoices() {
  if (typeof window === 'undefined' || !window.speechSynthesis) return [];
  return window.speechSynthesis.getVoices();
}

export function speechSupported() {
  return (
    typeof window !== 'undefined' &&
    ('speechSynthesis' in window || typeof Audio !== 'undefined')
  );
}

function isFemale(voice) {
  if (MALE.test(voice.name) && !FEMALE.test(voice.name)) return false;
  return FEMALE.test(voice.name);
}

export function listVoices(lang) {
  if (lang === 'ar') return [];
  const list = allVoices()
    .filter((v) => v.lang.toLowerCase().replace('_', '-').startsWith('en'))
    .map((v) => ({
      uri: v.voiceURI,
      label: v.name,
      score: isFemale(v) ? 20 : 5,
      recommended: false,
    }))
    .sort((a, b) => b.score - a.score);
  if (list[0]) list[0].recommended = true;
  return list;
}

export function bestVoice(lang) {
  return listVoices(lang)[0];
}

export function stopSpeech() {
  token += 1;
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.src = '';
    currentAudio = null;
  }
  if (typeof window !== 'undefined' && window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
}

function synthesizeArabic(text) {
  if (!audioCache.has(text)) {
    if (audioCache.size >= AUDIO_CACHE_LIMIT) audioCache.delete(audioCache.keys().next().value);
    const pending = synthesizeSpeech(text, ARABIC_VOICE)
      .then((res) => {
        if (!res?.audioContent) throw new Error('No audio returned');
        return res.audioContent;
      })
      .catch((error) => {
        audioCache.delete(text); // allow a retry later
        throw error;
      });
    audioCache.set(text, pending);
  }
  return audioCache.get(text);
}

// Warm the cache so the next question starts speaking without a network wait.
export function prefetchSpeech(text, lang) {
  if (lang !== 'ar' || !text?.trim()) return;
  synthesizeArabic(text.trim()).catch(() => {});
}

async function speakArabic(text) {
  const my = token;
  try {
    const audioContent = await synthesizeArabic(text);
    if (my !== token) return; // the user moved on while we waited
    const audio = new Audio(`data:audio/mp3;base64,${audioContent}`);
    currentAudio = audio;
    await audio.play();
  } catch (error) {
    // Narration is optional; never break the quiz over it.
  }
}

function speakBrowser(text, voiceUri, rate) {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  const utter = new SpeechSynthesisUtterance(text);
  const voice =
    (voiceUri && allVoices().find((v) => v.voiceURI === voiceUri)) ||
    allVoices().find((v) => v.lang.toLowerCase().startsWith('en') && isFemale(v)) ||
    allVoices().find((v) => v.lang.toLowerCase().startsWith('en'));
  if (voice) {
    utter.voice = voice;
    utter.lang = voice.lang;
  } else {
    utter.lang = 'en-US';
  }
  utter.rate = rate ?? 0.95;
  utter.pitch = 1;
  window.speechSynthesis.resume();
  window.speechSynthesis.speak(utter);
}

export function speak(text, lang, opts) {
  stopSpeech();
  const line = String(text || '').trim();
  if (!line) return;
  if (lang === 'ar') speakArabic(line);
  else speakBrowser(line, opts?.voiceUri, opts?.rate);
}

export function preloadVoices(onChange) {
  if (onChange) onChange();
  if (typeof window === 'undefined' || !window.speechSynthesis) return () => {};
  const notify = () => onChange && onChange();
  window.speechSynthesis.addEventListener('voiceschanged', notify);
  window.speechSynthesis.getVoices();
  return () => window.speechSynthesis.removeEventListener('voiceschanged', notify);
}
